/**
 * SoftSkillsPlayerPage — v10.7.2 Fase 2c
 * Player interactivo para escenarios `scenario_type=soft_skills`.
 *
 * Muestra:
 *   - Interlocutor + estado emocional en vivo (trust, anxiety, anger, ...)
 *   - Etapa actual del protocolo (SPIKES u otro)
 *   - Choices seleccionables (buena práctica / error crítico se ven al debriefing)
 *   - Respuesta del interlocutor tras cada choice
 *   - Barra de avance por etapas
 *   - Al terminar: score + timeline
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ChatCircle, User, ArrowRight, Trophy, WarningCircle, CheckCircle, Repeat, Timer, Sparkle } from '@phosphor-icons/react';

const STATE_META = {
  trust:               { label: 'Confianza',       color: '#10B981' },
  anxiety:             { label: 'Ansiedad',        color: '#F59E0B' },
  anger:               { label: 'Enojo',           color: '#EF4444' },
  understanding:       { label: 'Comprensión',     color: '#06B6D4' },
  cooperation:         { label: 'Cooperación',     color: '#8B5CF6' },
  rapport:             { label: 'Rapport',         color: '#EC4899' },
  emotional_distress:  { label: 'Distrés',         color: '#F97316' },
  satisfaction:        { label: 'Satisfacción',    color: '#22C55E' },
  sadness:             { label: 'Tristeza',        color: '#6366F1' },
};

const KEY_METRICS = ['trust', 'anxiety', 'anger', 'understanding'];

function EmotionalBar({ metric, value }) {
  const meta = STATE_META[metric] || { label: metric, color: '#94A3B8' };
  return (
    <div data-testid={`state-${metric}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-700">{meta.label}</span>
        <span className="text-xs font-mono text-slate-900">{Math.round(value)}</span>
      </div>
      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${value}%`, backgroundColor: meta.color }}
        />
      </div>
    </div>
  );
}

export default function SoftSkillsPlayerPage() {
  const { scenarioId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // v10.7.3 Rehearsal Mode: `?mode=rehearsal&stage=E` — loop 3 min sobre una etapa
  const rehearsalMode = searchParams.get('mode') === 'rehearsal';
  const rehearsalStageId = searchParams.get('stage') || 'E';
  const REHEARSAL_SECONDS = 180;

  const [scenario, setScenario] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [state, setState] = useState({});
  const [currentStageId, setCurrentStageId] = useState(null);
  const [lastResponse, setLastResponse] = useState(null);
  const [lastFeedback, setLastFeedback] = useState(null);
  const [stagesCompleted, setStagesCompleted] = useState([]);
  const [choicesLocked, setChoicesLocked] = useState(false);
  const [finished, setFinished] = useState(false);
  const [score, setScore] = useState(null);
  const [rehearsalStats, setRehearsalStats] = useState({ attempts: 0, good: 0, critical: 0, timeLeft: REHEARSAL_SECONDS });
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiThinking, setAiThinking] = useState(false);

  // Fetch scenario + init session
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/scenarios/${scenarioId}`);
        const s = res.data;
        if ((s.scenario_type || 'clinical') !== 'soft_skills') {
          toast.error('Este escenario no es de Habilidades Blandas');
          navigate('/app/scenarios');
          return;
        }
        setScenario(s);
        const cfg = s.soft_skills_config || {};
        const initial = (cfg.interlocutor || {}).initial_state || {};
        setState({ trust: 50, anxiety: 50, anger: 0, understanding: 50, cooperation: 50, rapport: 50, emotional_distress: 40, sadness: 30, satisfaction: 50, ...initial });
        // Rehearsal mode: forzar la etapa configurada si existe, sino primera
        const stages = cfg.stages || [];
        const startStage = rehearsalMode
          ? (stages.find((st) => st.stage_id === rehearsalStageId) || stages[0])
          : stages[0];
        setCurrentStageId(startStage?.stage_id || null);
        // Create session
        const sessRes = await api.post('/simulations/start', { scenario_id: scenarioId });
        setSessionId(sessRes.data?.id || sessRes.data?.session_id || `local-${Date.now()}`);
      } catch (e) {
        console.error(e);
        toast.error('No se pudo iniciar el escenario');
      }
    })();
  }, [scenarioId, navigate]);

  const cfg = scenario?.soft_skills_config || {};
  const stages = cfg.stages || [];
  const currentStage = useMemo(() => stages.find((s) => s.stage_id === currentStageId), [stages, currentStageId]);
  const totalStages = stages.length || 1;
  const progressPct = Math.round((stagesCompleted.length / totalStages) * 100);

  // Timer para rehearsal mode
  useEffect(() => {
    if (!rehearsalMode || finished || !sessionId) return;
    if (rehearsalStats.timeLeft <= 0) {
      finishSession();
      return;
    }
    const t = setInterval(() => {
      setRehearsalStats((prev) => ({ ...prev, timeLeft: Math.max(0, prev.timeLeft - 1) }));
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rehearsalMode, finished, sessionId, rehearsalStats.timeLeft]);

  const submit = async (choice) => {
    if (!sessionId || choicesLocked) return;
    setChoicesLocked(true);
    if (aiEnabled) setAiThinking(true);
    try {
      const { data } = await api.post('/soft-skills/play/choice', {
        session_id: sessionId,
        stage_id: currentStageId,
        choice,
        use_ai: aiEnabled,
      });
      setAiThinking(false);
      setState(data.state);
      setLastResponse(data.response);
      setLastFeedback({
        good: data.is_good_practice,
        bad: data.is_critical_error,
      });
      if (rehearsalMode) {
        setRehearsalStats((prev) => ({
          ...prev,
          attempts: prev.attempts + 1,
          good: prev.good + (data.is_good_practice ? 1 : 0),
          critical: prev.critical + (data.is_critical_error ? 1 : 0),
        }));
      } else {
        setStagesCompleted((prev) => prev.includes(currentStageId) ? prev : [...prev, currentStageId]);
      }
      // Avanzar tras breve pausa para que el usuario vea el feedback
      setTimeout(() => {
        setLastResponse(null);
        setLastFeedback(null);
        setChoicesLocked(false);
        if (rehearsalMode) {
          // Rehearsal: resetear estado al inicial y quedarse en la misma etapa
          const cfg = scenario?.soft_skills_config || {};
          const initial = (cfg.interlocutor || {}).initial_state || {};
          setState({ trust: 50, anxiety: 50, anger: 0, understanding: 50, cooperation: 50, rapport: 50, emotional_distress: 40, sadness: 30, satisfaction: 50, ...initial });
          return; // No cambiar de etapa
        }
        if (data.next_stage_id && stages.find((s) => s.stage_id === data.next_stage_id)) {
          setCurrentStageId(data.next_stage_id);
        } else {
          finishSession();
        }
      }, 2600);
    } catch (e) {
      toast.error('Error al enviar respuesta');
      setChoicesLocked(false);
      setAiThinking(false);
    }
  };

  const finishSession = async () => {
    setFinished(true);
    try {
      const { data } = await api.post(`/soft-skills/sessions/${sessionId}/score`);
      setScore(data);
    } catch (e) {
      /* silent */
    }
  };

  if (!scenario) {
    return <div className="p-8 text-slate-500">Cargando escenario…</div>;
  }

  const interlocutor = cfg.interlocutor || {};

  if (finished) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
            <Trophy size={30} className="text-emerald-500" weight="fill" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">{rehearsalMode ? 'Ensayo terminado' : 'Escenario completado'}</h1>
          <p className="text-slate-500 text-sm">{scenario.nombre}{rehearsalMode ? ` · Etapa ${rehearsalStageId}` : ''}</p>
        </div>
        {rehearsalMode ? (
          <Card>
            <CardContent className="p-6 space-y-4" data-testid="rehearsal-summary">
              <div className="text-center">
                <div className="text-5xl font-bold text-amber-600">{rehearsalStats.attempts}</div>
                <div className="text-slate-500 text-sm">Intentos totales en 3 min</div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div><div className="text-xs text-slate-500">Buenas prácticas</div><div className="text-xl font-bold text-emerald-600">{rehearsalStats.good}</div></div>
                <div><div className="text-xs text-slate-500">Errores críticos</div><div className="text-xl font-bold text-red-600">{rehearsalStats.critical}</div></div>
                <div><div className="text-xs text-slate-500">Ratio de acierto</div><div className="text-xl font-bold text-slate-900">{rehearsalStats.attempts ? Math.round(100 * rehearsalStats.good / rehearsalStats.attempts) : 0}%</div></div>
                <div><div className="text-xs text-slate-500">Etapa</div><div className="text-xl font-bold text-purple-700">{rehearsalStageId}</div></div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={() => window.location.reload()} variant="outline" className="flex-1" data-testid="rehearsal-restart">
                  <Repeat size={16} className="mr-2" /> Otra ronda
                </Button>
                <Button onClick={() => navigate('/app/scenarios')} className="flex-1" data-testid="rehearsal-back">
                  Volver a escenarios
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : score && (
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="text-center">
                <div className="text-5xl font-bold text-cyan-600">{score.final_score}</div>
                <div className="text-slate-500 text-sm">Puntaje final</div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div><div className="text-xs text-slate-500">Completitud</div><div className="text-xl font-bold text-slate-900">{score.completion_pct}%</div></div>
                <div><div className="text-xs text-slate-500">Calidad</div><div className="text-xl font-bold text-slate-900">{score.quality_pct}%</div></div>
                <div><div className="text-xs text-slate-500">Errores críticos</div><div className="text-xl font-bold text-red-600">{score.critical_errors}</div></div>
                <div><div className="text-xs text-slate-500">Buenas prácticas</div><div className="text-xl font-bold text-emerald-600">{score.good_practices}</div></div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => navigate(`/app/soft-skills/debrief/${sessionId}`)} className="flex-1" data-testid="soft-skills-view-debrief">
                  Ver debriefing emocional
                </Button>
                <Button onClick={() => navigate('/app/scenarios')} className="flex-1" data-testid="soft-skills-finish-back">
                  Volver a escenarios
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Badge className="bg-purple-100 text-purple-700 border-purple-200">Habilidades Blandas</Badge>
            {rehearsalMode && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1" data-testid="rehearsal-mode-badge">
                <Repeat size={12} weight="bold" /> Modo Ensayo · Etapa {rehearsalStageId}
              </Badge>
            )}
          </div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="soft-skills-title">{scenario.nombre}</h1>
          <p className="text-sm text-slate-600 mt-1">{rehearsalMode ? `Practica exclusivamente la etapa ${rehearsalStageId}. El estado se reinicia tras cada respuesta.` : scenario.briefing}</p>
        </div>
        {rehearsalMode && (
          <div className="shrink-0 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center" data-testid="rehearsal-timer">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-600">
              <Timer size={12} weight="fill" /> Restante
            </div>
            <div className="text-2xl font-mono font-bold text-amber-700">
              {Math.floor(rehearsalStats.timeLeft / 60)}:{String(rehearsalStats.timeLeft % 60).padStart(2, '0')}
            </div>
            <div className="text-[10px] text-amber-600 mt-1">
              {rehearsalStats.attempts} intentos · <span className="text-emerald-600 font-semibold">{rehearsalStats.good} buenas</span> · <span className="text-red-600 font-semibold">{rehearsalStats.critical} críticos</span>
            </div>
          </div>
        )}
        {!rehearsalMode && (
          <button
            onClick={() => setAiEnabled((v) => !v)}
            className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${aiEnabled ? 'bg-violet-50 border-violet-300 text-violet-700 shadow-sm' : 'bg-white border-slate-200 text-slate-500 hover:border-violet-200'}`}
            data-testid="ai-toggle"
            title="Genera respuestas del interlocutor con Claude usando su personalidad y estado emocional actual"
          >
            <Sparkle size={16} weight={aiEnabled ? 'fill' : 'regular'} />
            <div className="text-left">
              <div className="text-xs font-semibold uppercase tracking-widest">IA generativa</div>
              <div className="text-[10px]">{aiEnabled ? 'Activada · Claude Sonnet' : 'Activar respuestas dinámicas'}</div>
            </div>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left col: interlocutor + emotional state */}
        <Card className="lg:col-span-1">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                <User size={28} className="text-white" weight="fill" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{interlocutor.name || 'Interlocutor'}</div>
                <div className="text-xs text-slate-500">{interlocutor.role}</div>
                {interlocutor.personality && (
                  <div className="text-[11px] text-slate-400 italic mt-0.5 truncate">{interlocutor.personality}</div>
                )}
              </div>
            </div>
            <div className="pt-3 border-t border-slate-100 space-y-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                Estado emocional
              </div>
              {KEY_METRICS.map((k) => (
                <EmotionalBar key={k} metric={k} value={state[k] ?? 0} />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Right col: current stage */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <div className="mb-3">
              {rehearsalMode ? (
                <div className="text-xs font-semibold uppercase tracking-widest text-amber-600 mb-2">
                  Modo ensayo — Repite esta etapa hasta agotar el tiempo
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                      Etapa {stagesCompleted.length + (lastResponse ? 0 : 1)} de {totalStages}
                    </span>
                    <span className="text-xs text-slate-500">{progressPct}%</span>
                  </div>
                  <Progress value={progressPct} className="h-1.5" />
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {stages.map((s) => (
                      <span
                        key={s.stage_id}
                        className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                          stagesCompleted.includes(s.stage_id) ? 'bg-emerald-100 text-emerald-700' :
                          s.stage_id === currentStageId ? 'bg-cyan-100 text-cyan-700 ring-1 ring-cyan-400' :
                          'bg-slate-100 text-slate-500'
                        }`}
                      >{s.stage_id}</span>
                    ))}
                  </div>
                </>
              )}
            </div>

            {aiThinking ? (
              <div className="space-y-3">
                <div className="p-4 rounded-lg bg-violet-50 border border-violet-200 flex items-center gap-3" data-testid="ai-thinking">
                  <Sparkle size={20} className="text-violet-500 animate-pulse" weight="fill" />
                  <div>
                    <div className="text-[10px] font-bold uppercase text-violet-600 tracking-widest">Claude está generando la respuesta…</div>
                    <div className="text-xs text-violet-700 mt-0.5 italic">Considerando personalidad + estado emocional actual</div>
                  </div>
                </div>
              </div>
            ) : lastResponse ? (
              <div className="space-y-3">
                {lastFeedback?.bad && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
                    <WarningCircle size={18} className="mt-0.5 shrink-0" weight="fill" />
                    <span><strong>Error crítico registrado.</strong> Se reflejará en el debriefing.</span>
                  </div>
                )}
                {lastFeedback?.good && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                    <CheckCircle size={18} className="mt-0.5 shrink-0" weight="fill" />
                    <span><strong>Buena práctica.</strong> Impacto positivo en el interlocutor.</span>
                  </div>
                )}
                <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                  <div className="flex items-start gap-3">
                    <ChatCircle size={20} className="text-purple-600 mt-0.5" weight="fill" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-[10px] font-bold uppercase text-purple-600 tracking-widest">{interlocutor.name}</div>
                        {lastResponse.generated_by_ai && (
                          <Badge className="bg-violet-100 text-violet-700 border-violet-200 gap-1 text-[9px] px-1.5 py-0" data-testid="ai-generated-badge">
                            <Sparkle size={9} weight="fill" /> IA
                          </Badge>
                        )}
                      </div>
                      <div className="text-slate-900 mt-1 italic">"{lastResponse.text}"</div>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-slate-400 text-center animate-pulse">Continuando en instantes…</div>
              </div>
            ) : currentStage ? (
              <div className="space-y-3">
                <div className="text-slate-800 font-medium text-base leading-relaxed">
                  {currentStage.prompt}
                </div>
                <div className="space-y-2 pt-2" data-testid="soft-skills-choices">
                  {(currentStage.choices || []).map((c, idx) => (
                    <button
                      key={c.id || idx}
                      disabled={choicesLocked}
                      onClick={() => submit(c)}
                      data-testid={`choice-${c.id || idx}`}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        choicesLocked
                          ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                          : 'border-slate-200 hover:border-cyan-500 hover:bg-cyan-50/50 cursor-pointer bg-white'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-slate-400 font-mono text-sm mt-0.5">{String.fromCharCode(65 + idx)}.</span>
                        <span className="text-sm text-slate-800 flex-1">{c.label}</span>
                        <ArrowRight size={16} className="text-slate-300 shrink-0 mt-0.5" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-slate-500 text-sm">Sin etapa activa.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
